import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Crown,
  Gift,
  Loader2,
  Star,
  Check,
  History,
  Award,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoyaltyPage() {
  const navigate = useNavigate();
  const { isAuthenticated, token, user, loading: authLoading } = useAuth();
  const { t, language } = useLanguage();
  
  const [rewards, setRewards] = useState([]);
  const [loyaltyData, setLoyaltyData] = useState({ points: 0 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login?redirect=/loyalty");
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchData();
    }
  }, [isAuthenticated, token]);

  const fetchData = async () => {
    try {
      const [rewardsRes, pointsRes, historyRes] = await Promise.all([
        axios.get(`${API}/loyalty/rewards`),
        axios.get(`${API}/loyalty/points`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/loyalty/history`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setRewards(rewardsRes.data);
      setLoyaltyData(pointsRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (rewardId) => {
    setRedeeming(rewardId);
    try {
      const response = await axios.post(
        `${API}/loyalty/redeem/${rewardId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(language === "de" ? "Prämie eingelöst!" : "Reward redeemed!");
      setLoyaltyData({ ...loyaltyData, points: response.data.remaining_points });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to redeem";
      toast.error(message);
    } finally {
      setRedeeming(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  const isPremium = user?.membership_type === "premium";

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="loyalty-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-4 font-['Chivo']">
            {t("loyalty_title")} <Star className="inline h-8 w-8 text-cyan-400" />
          </h1>
          <p className="text-zinc-400">
            {language === "de" 
              ? "Sammeln Sie Punkte bei jeder Buchung und lösen Sie diese gegen tolle Prämien ein."
              : "Earn points with every booking and redeem them for great rewards."}
          </p>
        </div>

        {/* Points Card */}
        <div className={`mb-8 p-8 rounded-sm ${isPremium ? "membership-premium bg-zinc-900" : "bg-zinc-900 border border-zinc-800"}`}>
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <Award className="h-10 w-10 text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-zinc-500 mb-1">{t("loyalty_points")}</p>
                <p className="text-5xl font-black text-white">{loyaltyData.points}</p>
                {isPremium && (
                  <p className="text-xs text-cyan-400 mt-1 flex items-center gap-1">
                    <Crown className="h-3 w-3" />
                    2x {language === "de" ? "Punkte bei jeder Buchung" : "points on every booking"}
                  </p>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-zinc-500">
                {language === "de" ? "So sammeln Sie Punkte:" : "How to earn points:"}
              </p>
              <ul className="text-sm text-zinc-400 mt-2 space-y-1">
                <li>• {language === "de" ? "Buchung abschließen: 10-50 Punkte" : "Complete booking: 10-50 points"}</li>
                <li>• {language === "de" ? "Bewertung schreiben: 5 Punkte" : "Write review: 5 points"}</li>
                {isPremium && <li className="text-cyan-400">• Premium: 2x Punkte!</li>}
              </ul>
            </div>
          </div>
        </div>

        {/* Rewards Grid */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <Gift className="h-5 w-5 text-cyan-400" />
            {t("loyalty_rewards")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {rewards.map((reward) => {
              const canRedeem = loyaltyData.points >= reward.points_required;
              return (
                <div
                  key={reward.id}
                  className={`bg-zinc-900 border rounded-sm p-6 ${
                    canRedeem ? "border-cyan-500/50" : "border-zinc-800"
                  }`}
                  data-testid={`reward-${reward.id}`}
                >
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {language === "de" ? reward.name_de : reward.name}
                    </h3>
                    <p className="text-sm text-zinc-500">
                      {language === "de" ? reward.description_de : reward.description}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Star className="h-5 w-5 text-cyan-400" />
                      <span className="text-lg font-bold text-white">
                        {reward.points_required}
                      </span>
                    </div>
                    <Button
                      onClick={() => handleRedeem(reward.id)}
                      disabled={!canRedeem || redeeming === reward.id}
                      className={canRedeem 
                        ? "bg-cyan-500 hover:bg-cyan-400 text-black" 
                        : "bg-zinc-700 text-zinc-400 cursor-not-allowed"}
                      data-testid={`redeem-${reward.id}`}
                    >
                      {redeeming === reward.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : canRedeem ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          {t("loyalty_redeem")}
                        </>
                      ) : (
                        t("loyalty_not_enough")
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* History */}
        {history.length > 0 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <History className="h-5 w-5 text-cyan-400" />
              {t("loyalty_history")}
            </h2>
            <div className="space-y-3">
              {history.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm"
                >
                  <div>
                    <p className="text-sm text-white">
                      {rewards.find(r => r.id === item.reward_id)?.name_de || item.reward_id}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {new Date(item.redeemed_at).toLocaleDateString(language === "de" ? "de-DE" : "en-US")}
                    </p>
                  </div>
                  <span className="text-sm text-red-400">-{item.points_spent} Punkte</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
